import { apiErrorCode } from './apiErrorCode';
import { humanizeError } from './errors';

/**
 * Ручной зачёт урока и «Открыть следующий» (договор `docs/CONTRACTS/lesson-force-complete.md`,
 * решения владельца 13.09.2026).
 *
 * - Курс «оценивает куратор» — отдельной кнопки нет: оценка у НЕпройденного урока засчитывает его.
 * - Курс «оценивает система» и мини-курс — «Открыть следующий»: текущий урок остаётся непройденным.
 *
 * Здесь только то, что проверяется без экрана.
 */

type T = (key: string, vars?: Record<string, unknown>) => string;

type LessonLike = {
  lesson_id: string;
  module_title?: string;
  is_completed: boolean;
  next_opened?: unknown;
};

/**
 * Уроки, на которых ученик стоит: первый непройденный урок каждого модуля без отметки
 * «следующий открыт».
 *
 * ⚠️ ТОЛЬКО ОН, А НЕ ЛЮБОЙ НЕПРОЙДЕННЫЙ (договор §3). Отметка не перескакивает цепочку:
 * «Открыть следующий» на уроке, до которого ученик ещё не дошёл, ничего видимого не даст.
 * А зачёт оценкой дальше по цепочке засчитал бы урок, которого ученик даже не видел. Цепочка
 * начинается заново в каждом модуле, поэтому кандидат — на модуль.
 *
 * Урок с отметкой «следующий открыт» цепочку продолжает, как пройденный: ученик стоит уже
 * на следующем.
 *
 * Список уроков сервер отдаёт в порядке курса; модуль — по `module_title`.
 */
export function stuckLessonIds(lessons: LessonLike[]): Set<string> {
  const out = new Set<string>();
  const doneModules = new Set<string>();
  for (const l of lessons) {
    const module = l.module_title ?? '';
    if (doneModules.has(module)) continue;
    if (l.is_completed || hasNextOpened(l)) continue;
    out.add(l.lesson_id);
    doneModules.add(module);
  }
  return out;
}

/** Отметка «следующий открыт» — объект с временем; `null` и мусор — отметки нет. */
export function hasNextOpened(l: { next_opened?: unknown }): boolean {
  return parseNextOpened(l.next_opened) !== null;
}

export type NextOpened = { name: string | null; at: string };

export function parseNextOpened(raw: unknown): NextOpened | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as { at?: unknown; by?: unknown };
  if (typeof r.at !== 'string' || r.at === '') return null;
  const by = r.by && typeof r.by === 'object' ? (r.by as { name?: unknown }) : null;
  const name = by && typeof by.name === 'string' && by.name.trim() !== '' ? by.name : null;
  return { name, at: r.at };
}

/**
 * Пометка рядом с «Засчитан вручную» (26.09.2026, вариант В разбора
 * `docs/CHECK-2026-09-26-KHABIBULLA-ANSWERS.md`).
 *
 * Куратор засчитывал оценкой уроки, которых ученик не открывал, а потом видел пустой разбор и
 * думал, что ответы пропали. Пометка говорит правду заранее: `notOpened` — ни ответа, ни следа
 * в уроке; `noAnswers` — ответов нет, но ученик в урок заходил.
 *
 * ⚠️ НЕТ ПОЛЯ — НЕТ И ПОМЕТКИ. Старый сервер `answers_count` не шлёт, и «0» там был бы выдумкой.
 * Урок, пройденный самим учеником, пометки не получает: там вопросов нет.
 */
export type ManualCompletionNote = 'notOpened' | 'noAnswers';

export function manualCompletionNote(l: {
  completed_manually?: boolean;
  answers_count?: number;
  opened?: boolean;
}): ManualCompletionNote | null {
  if (!l.completed_manually || typeof l.answers_count !== 'number' || l.answers_count > 0) return null;
  return l.opened === false ? 'notOpened' : 'noAnswers';
}

/** Ключ словаря для пометки. */
export const MANUAL_NOTE_KEYS: Record<ManualCompletionNote, string> = {
  notOpened: 'studentProgress.manualNotOpened',
  noAnswers: 'studentProgress.manualNoAnswers',
};

/**
 * Отказы ручек зачёта и «Открыть следующий» (§2.1, §3.2).
 *
 * ⚠️ РАЗЛИЧАТЬ ПО `code`. Три разных 409 значат разное, и выход у них разный: курс перевели в
 * другой режим, пока сотрудник смотрел, — обновить страницу; урок уже пройден — делать ничего не
 * надо; без цифры засчитывать нечего — поставить оценку.
 */
const CODE_KEYS: Record<string, string> = {
  grading_mode_auto: 'studentProgress.gradingModeAuto',
  grading_mode_curator: 'studentProgress.errGradingModeCurator',
  lesson_already_completed: 'studentProgress.errAlreadyCompleted',
  lesson_not_completed: 'studentProgress.errNeedsGrade',
  lesson_not_found: 'studentProgress.errLessonNotFound',
};

export function manualActionErrorText(err: unknown, t: T): string {
  const code = apiErrorCode(err);
  const key = code ? CODE_KEYS[code] : undefined;
  return key ? t(key) : humanizeError(err);
}
